import { Typography } from "@mui/material";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { RemoveCircle } from "@mui/icons-material";
import ButtonSplit from "../../../../common/components/functional/buttons/ButtonSplit";
import { CancelPayMenu } from "../../components/CancelPayMenu";
import { CancelAssignmentScope } from "../../constants";
import { AssignedTalent } from "../../orderAPI";
import { OrderFormShortRowGridItem } from "../../styles";
import {
  CancelAllAssignmentsCallback,
  CancelAssignmentCallback,
} from "../../types/editOrder";
import ShiftTalentInfoLink from "./ShiftTalentInfoLink";

type Props = {
  talent: AssignedTalent;
  assignmentId: string;
  rowId: string;
  cancelAssignment: CancelAssignmentCallback;
  canCancelAllShifts: boolean;
  cancelAllAssignments: CancelAllAssignmentsCallback;
};

export const ShiftTalentInfo = ({
  talent,
  assignmentId,
  rowId,
  cancelAssignment,
  canCancelAllShifts,
  cancelAllAssignments,
}: Props) => {
  const { t } = useTranslation("order");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMode, setMenuMode] = useState<CancelAssignmentScope | null>(null);

  const handleOpenMenu = (
    event: MouseEvent<HTMLButtonElement | HTMLLIElement>,
    mode: CancelAssignmentScope,
    anchorElOverride?: HTMLElement | null,
  ) => {
    setMenuMode(mode);
    setAnchorEl(anchorElOverride || event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuMode(null);
  };

  const handlePaySelection = (paySalary: boolean) => {
    if (menuMode === CancelAssignmentScope.Single) {
      cancelAssignment(assignmentId, rowId, paySalary);
    }
    if (menuMode === CancelAssignmentScope.Bundle) {
      cancelAllAssignments(talent.id, paySalary);
    }
    handleClose();
  };

  const cancelAssignmentButtons = () => {
    const cancelOptions = [
      {
        id: "cancelShift",
        label: t("cancelShift"),
        onClick: (
          event: MouseEvent<HTMLButtonElement | HTMLLIElement>,
          anchorElOverride: HTMLElement | null,
        ) =>
          handleOpenMenu(event, CancelAssignmentScope.Single, anchorElOverride),
      },
      {
        id: "cancelAllShifts",
        label: t("cancelAllShifts"),
        disabled: !canCancelAllShifts,
        onClick: (
          event: MouseEvent<HTMLButtonElement | HTMLLIElement>,
          anchorElOverride: HTMLElement | null,
        ) =>
          handleOpenMenu(event, CancelAssignmentScope.Bundle, anchorElOverride),
      },
    ];

    return (
      <>
        <ButtonSplit
          options={cancelOptions}
          variant="outlined"
          color="error"
          sx={{ marginLeft: "8px" }}
          buttonSx={{ borderRadius: "20px" }}
        />
        <CancelPayMenu
          id="cancel-pay-menu"
          anchorEl={anchorEl}
          onClose={handleClose}
          onSelect={handlePaySelection}
        />
      </>
    );
  };

  const cancellationInfo = () => {
    return (
      <>{` (${t("cancelled")} - ${
        talent.talentGetsPaid ? t("paidButNotBilled") : t("notPaid")
      } - ${t(talent.reasonForCancellation!!)})`}</>
    );
  };

  return (
    <OrderFormShortRowGridItem
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        gap: "0.5rem",
        whiteSpace: "nowrap",
        lineHeight: 1.2,
      }}
    >
      {talent.absent ? (
        <ShiftTalentInfoLink talent={talent}>
          <Typography sx={{ whiteSpace: "nowrap" }}>
            {`(${t("talentAbsent")})`}
          </Typography>
          <RemoveCircle sx={{ color: "#d32f2f" }} />
        </ShiftTalentInfoLink>
      ) : talent.reasonForCancellation ? (
        <ShiftTalentInfoLink talent={talent}>
          {cancellationInfo()}
        </ShiftTalentInfoLink>
      ) : (
        <ShiftTalentInfoLink talent={talent}>
          {cancelAssignmentButtons()}
        </ShiftTalentInfoLink>
      )}
    </OrderFormShortRowGridItem>
  );
};
